<script>
import DashboardLayout from "@/Layouts/DashboardLayout.vue";

export default {
    layout: DashboardLayout,
};
</script>

<script setup>
import { computed, onMounted, ref } from "vue";
import { Head } from "@inertiajs/vue3";
import { useAuthUser } from "@/composables/useAuthUser.js";
import { useModalogStore } from "@/stores/modalog.js";
import ActionButtons from "@/Components/Dashboard/ActionButtons.vue";
import UserListsWidget from "@/Components/Dashboard/UserListsWidget.vue";
import BrowserExtensionCta from "@/Components/Dashboard/BrowserExtensionCta.vue";
import HeroPromos from "@/Components/Dashboard/HeroPromos.vue";
import ExpertListsSection from "@/Components/Dashboard/ExpertListsSection.vue";
import DiscoverExpertsSection from "@/Components/Dashboard/DiscoverExpertsSection.vue";

const props = defineProps({
    lists: {
        type: Object,
        default: () => ({ items: [], total: 0, view_all_url: null }),
    },
    savedLists: {
        type: Object,
        default: () => ({ items: [], view_all_url: null }),
    },
    expertLists: {
        type: Array,
        default: () => [],
    },
    expertListTabs: {
        type: Array,
        default: () => ["Featured"],
    },
    experts: {
        type: Array,
        default: () => [],
    },
    heroPromos: {
        type: Object,
        required: true,
    },
});

// The add-gift flow needs a destination list; default to the newest one.
const defaultList = computed(() => props.lists.items[0] ?? null);

const user = useAuthUser();

// Expert-list bookmark in flight (GH-1159): the Saved Lists widget shows
// it as a ghost row (adding) or throbs the matching row (removing) until
// the partial reload replaces the list.
const pendingBookmark = ref(null);
const modalogStore = useModalogStore();

// First-run onboarding tour (GH-1056): once per account (GH-1335). The
// member record is stamped the moment the tour opens, so dismissing it any
// way counts as seen. Relaunch lives in the account dropdown.
onMounted(() => {
    if (user.value && !user.value.has_seen_tour) {
        axios.post(route("dashboard.tour-seen")).catch(() => {});
        modalogStore.showModalog({ type: "ModalogOnboardingTour", props: {} });
    }
});

// "Jeremy's Lists" when we know the name, else a neutral fallback.
const myListsTitle = computed(() =>
    user.value?.first_name ? `${user.value.first_name}'s Lists` : "My Lists",
);
</script>

<template>
    <div>
        <Head title="Dashboard" />

        <!--
            Mobile (single column) order is driven by `order-*`; from lg the two
            `contents` wrappers become real flex columns (sidebar + main). The
            `contents` display lets all sections flatten into one flex flow on
            mobile so they interleave per the mobile design. Between lg and xl
            the sidebar narrows to 300px and the action buttons stack (BH-29).
        -->
        <div class="flex flex-col gap-6 lg:flex-row xl:gap-7">
            <!-- Sidebar column -->
            <div
                class="contents lg:flex lg:w-[300px] lg:shrink-0 lg:flex-col lg:gap-5 xl:w-[352px]"
            >
                <ActionButtons :default-list="defaultList" class="order-1" />

                <!--
                    From lg the lists, saved lists and extension promo share one
                    cream card that grows to the main column's height (the row
                    stretches, so the cream runs to the bottom of the content).
                    On mobile the wrapper is `contents`, so the items keep
                    interleaving with the main column via order-*.
                -->
                <div
                    class="contents lg:order-2 lg:flex lg:flex-1 lg:flex-col lg:gap-10 lg:rounded-brand lg:bg-brand-cream lg:p-5"
                >
                    <!--
                        My Lists + Saved Lists share one cream card on mobile;
                        from lg this wrapper is `contents`, so they flatten into
                        the sidebar's cream card above.
                    -->
                    <div
                        class="order-3 flex flex-col gap-10 rounded-brand bg-brand-cream p-4 lg:contents"
                    >
                        <UserListsWidget
                            :title="myListsTitle"
                            :lists="lists.items"
                            :view-all-url="lists.view_all_url"
                        >
                            <template #empty>
                                You haven't created any lists yet. Create your
                                first list to start adding gifts.
                            </template>
                        </UserListsWidget>

                        <UserListsWidget
                            title="Saved Lists"
                            :lists="savedLists.items"
                            :view-all-url="savedLists.view_all_url"
                            saved
                            :pending="pendingBookmark"
                            empty-icon="images/dashboard/icon-saved-lists-empty.svg"
                        >
                            <template #empty>
                                You do not currently have any saved lists.
                                Explore Expert Lists or Influencers you follow
                                to save lists.
                            </template>
                        </UserListsWidget>
                    </div>

                    <!-- mt-auto pins the promo to the foot of the sidebar card from lg. -->
                    <BrowserExtensionCta class="order-7 lg:mt-auto" />
                </div>
            </div>

            <!--
                Main column: 48px between sections, 64px before Discover
                Experts (Figma 443-218). When this column sets the row height
                the sidebar card stretches to match, so the 16px bottom
                padding (the card's 20px minus the line-box slack under the
                12px expert copy) lands the last expert row's ink level with
                the pinned promo card. Discover Experts never sinks: when the
                sidebar is taller it simply stays 64px under Expert Lists.
            -->
            <div
                class="contents lg:flex lg:min-w-0 lg:flex-1 lg:flex-col lg:gap-12 lg:pb-4"
            >
                <HeroPromos :promos="heroPromos" class="order-2" />
                <ExpertListsSection
                    :lists="expertLists"
                    :tabs="expertListTabs"
                    class="order-5"
                    @bookmark-pending="pendingBookmark = $event"
                />
                <DiscoverExpertsSection
                    :experts="experts"
                    class="order-6 lg:mt-4"
                />
            </div>
        </div>
    </div>
</template>
